import { KpiStrip } from "../../common/components";
import { STATUS_PILL_CLASS } from "../../tasks/constants/tasksViewV2Constants";

const TASK_LIFECYCLE_STATES = [
  // Match Defects lifecycle palette for consistency
  { k: "Open", label: "Open", color: "#B03A2E" },
  { k: "In Progress", label: "In Progress", color: "#b08a3a" },
  { k: "Completed", label: "Completed", color: "#2F7D4F" },
];

const DEFECT_LIFECYCLE_STATES = [
  // Match DefectsViewV2 lifecycle palette
  { k: "Open", label: "Open", color: "#B03A2E" },
  { k: "In Progress", label: "In Progress", color: "#b08a3a" },
  { k: "Fixed", label: "Fixed", color: "#8B5A9B" },
  { k: "To Test", label: "To Test", color: "#2E5C8A" },
  { k: "Closed", label: "Closed", color: "#2F7D4F" },
  { k: "Rejected", label: "Rejected", color: "#777" },
];

function StatusPill({ status }) {
  if (!status) return <span className="mv2-stpill st-na">—</span>;
  return <span className={STATUS_PILL_CLASS[status] || "mv2-stpill st-na"}>{status}</span>;
}

function getInitials(name) {
  if (!name) return "?";
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
}

function avatarColor(name) {
  if (!name) return 1;
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return (h % 5) + 1;
}

function AssigneeCell({ name }) {
  if (!name) return <span style={{ color: "var(--mv2-text-faint)" }}>—</span>;
  return (
    <div className="mv2-assignee-cell">
      <div className={`mv2-assignee-avatar ac-${avatarColor(name)}`}>
        {getInitials(name)}
      </div>
      <span className="mv2-assignee-name" title={name}>{name}</span>
    </div>
  );
}

const UV_CARDS = [
  { key: "overdue_tasks",    label: "Overdue Tasks",     tone: "red" },
  { key: "overdue_defects",  label: "Overdue Defects",   tone: "red" },
  { key: "today_tasks",      label: "Tasks Due Today",   tone: "amber" },
  { key: "today_defects",    label: "Defects Due Today", tone: "amber" },
  { key: "upcoming_tasks",   label: "Upcoming Tasks",    tone: "green" },
  { key: "upcoming_defects", label: "Upcoming Defects",  tone: "default" },
];

function fmtDate(val) {
  if (!val) return null;
  const s = String(val);
  const iso = s.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return s;
  const [y, m, d] = iso.split("-");
  const yy = y.slice(2);
  const timeMatch = s.match(/[T ](\d{2}:\d{2})/);
  const time = timeMatch ? ` ${timeMatch[1]}` : "";
  return `${d}-${m}-${yy}${time}`;
}

function dayStart(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function parseIsoDateOnly(v) {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// Due-date based aging:
// > 0  : overdue by N days
//   0  : due today
// < 0  : due in N days
function dueAgingDays(dueDate) {
  const due = parseIsoDateOnly(dueDate);
  if (!due) return null;
  const today = dayStart(new Date());
  const ms = today.getTime() - dayStart(due).getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function ageClass(days) {
  if (days == null) return "";
  if (days <= 0) return "ok";
  if (days <= 7) return "warn";
  return "crit";
}

function AgingCell({ dueDate }) {
  const days = dueAgingDays(dueDate);
  if (days == null) return <span className="uv-td-empty-cell">—</span>;
  const cls = ageClass(days);
  const pct = Math.min(100, (Math.max(0, days) / 14) * 100);
  const label = days > 0 ? `${days}d` : days === 0 ? "Today" : `In ${Math.abs(days)}d`;
  return (
    <div className={`uv-aging uv-aging--${cls}`}>
      <span className="uv-aging-num">
        {label}
      </span>
      <span className="uv-aging-bar">
        <span style={{ width: `${pct}%` }} />
      </span>
      <span className="uv-aging-ticks">
        <span>0</span>
        <span>7d</span>
        <span>14d+</span>
      </span>
    </div>
  );
}

const ASSIGNEE_COL = {
  key:  "assigned_to_name",
  label: "Assignee",
  cell: (row) => <AssigneeCell name={row?.assigned_to_name} />,
};

const TASK_COLS = [
  { key: "project",           label: "Project" },
  { key: "feature",           label: "Feature" },
  { key: "sub_feature",       label: "Sub-Feature" },
  { key: "description",       label: "Description" },
  {
    key: "created_by_name",
    label: "Created By",
    cell: (row) => {
      const name = row?.created_by_name || row?.created_by_display_name
        || row?.creator_name || row?.owner_name || row?.created_by_user_name || null;
      return <AssigneeCell name={name} />;
    },
  },
  { key: "aging",             label: "Aging",      cell: (row) => <AgingCell dueDate={row?.customer_due_date || row?.due_date} /> },
  { key: "customer_due_date", label: "Due Date",   fmt: fmtDate },
  { key: "status",            label: "Status",     cell: (row) => <StatusPill status={row?.status} /> },
  { key: "team_name",         label: "Team" },
];

const DEFECT_COLS = [
  { key: "defect_code",      label: "Code" },
  { key: "title",            label: "Title" },
  { key: "customer_name",    label: "Customer" },
  { key: "project_name",     label: "Project" },
  { key: "severity",         label: "Severity" },
  { key: "priority",         label: "Priority" },
  { key: "due_date",         label: "Due Date", fmt: fmtDate },
  { key: "status",           label: "Status" },
  { key: "reported_by_name", label: "Reported By" },
];

function UVTable({ cols, rows, emptyMsg, onRowClick }) {
  if (!rows) return null;
  return (
    <div className="page-table-wrap uv-table-wrap">
      <table className="page-table">
        <thead>
          <tr>
            <th className="uv-th-num">#</th>
            {cols.map((c) => <th key={c.key}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={cols.length + 1} className="uv-td-empty">{emptyMsg}</td>
            </tr>
          ) : rows.map((row, i) => (
            <tr
              key={row.id || i}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={onRowClick ? { cursor: "pointer" } : undefined}
            >
              <td className="uv-td-num">{i + 1}</td>
              {cols.map((c) => {
                if (c.cell) {
                  return (
                    <td key={c.key}>
                      {c.cell(row)}
                    </td>
                  );
                }
                const raw = row[c.key];
                const display = c.fmt ? c.fmt(raw, row) : raw;
                return (
                  <td key={c.key} title={String(display ?? "")}>
                    {display ?? <span className="uv-td-empty-cell">—</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function buildStatusCounts(items, states) {
  const counts = Object.fromEntries(states.map((s) => [s.k, 0]));
  const seen = new Set();
  for (const row of items || []) {
    const id = row?.id || row?.task_id || row?.defect_id || `${row?.description || ""}-${row?.due_date || row?.customer_due_date || ""}`;
    if (seen.has(id)) continue;
    seen.add(id);
    const status = row?.status || row?.tmm_impl_status || "";
    if (status in counts) counts[status] += 1;
  }
  return counts;
}

function LifecycleCard({ title, hint, flowText, states, counts }) {
  const total = Object.values(counts || {}).reduce((a, b) => a + b, 0);
  return (
    <div className="dfv2-lifecycle-wrap uv-lifecycle-card">
      <div className="dfv2-lifecycle-head">
        <h3>{title}</h3>
        {hint ? <span className="eyebrow">{hint}</span> : null}
        <span className="lc-rule" />
        <span className="hint">{flowText}</span>
      </div>
      <div className="dfv2-lifebar-wrap">
        <div className="dfv2-lifebar">
          {states.map((state) => {
            const n = counts?.[state.k] || 0;
            const flex = total > 0 ? n : 1;
            const isSmall = total > 0 && (n / total) < 0.03;
            return (
              <div
                key={state.k}
                className={`seg${isSmall ? " small" : ""}`}
                style={{ flex: `${flex} 1 0`, background: state.color }}
                title={`${state.label}: ${n}`}
              >
                <span className="n">{n}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="dfv2-lifebar-legend">
        {states.map((state) => {
          const n = counts?.[state.k] || 0;
          const pct = total > 0 ? ((n / total) * 100).toFixed(1) : "0.0";
          return (
            <div key={state.k} className="lg" style={{ "--lg-color": state.color }}>
              <span className="lb">{state.label}</span>
              <span className="n">{n}</span>
              <span className="pct">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function UserView({
  data,
  loading,
  activeCard,
  setActiveCard,
  includeAssignee = false,
  onRowClick,
}) {
  const isTask = activeCard.endsWith("_tasks");
  const baseCols = isTask ? TASK_COLS : DEFECT_COLS;
  const cols   = includeAssignee ? [ASSIGNEE_COL, ...baseCols] : baseCols;
  const rows   = data?.[activeCard] ?? [];
  const card   = UV_CARDS.find((c) => c.key === activeCard);

  const taskRows = [
    ...(data?.overdue_tasks || []),
    ...(data?.today_tasks || []),
    ...(data?.upcoming_tasks || []),
  ];
  const defectRows = [
    ...(data?.overdue_defects || []),
    ...(data?.today_defects || []),
    ...(data?.upcoming_defects || []),
  ];
  const taskStatusCounts = buildStatusCounts(taskRows, TASK_LIFECYCLE_STATES);
  const defectStatusCounts = buildStatusCounts(defectRows, DEFECT_LIFECYCLE_STATES);

  const kpiItems = UV_CARDS.map((c) => {
    const count = data ? (data[c.key]?.length ?? 0) : "—";
    return {
      key:     c.key,
      label:   c.label,
      value:   loading ? "…" : count,
      tone:    c.tone,
      sub:     activeCard === c.key ? "Viewing below" : "Click to view",
      onClick: () => setActiveCard(c.key),
      active:  activeCard === c.key,
    };
  });

  return (
    <div className="uv-section">
      <KpiStrip items={kpiItems} />

      <div className="uv-lifecycle-grid">
        <LifecycleCard
          title="Lifecycle"
          hint="Tasks"
          flowText={<>Flow → <b>OPEN</b> → in progress → completed</>}
          states={TASK_LIFECYCLE_STATES}
          counts={taskStatusCounts}
        />
        <LifecycleCard
          title="Lifecycle"
          hint="Defects"
          flowText={<>Flow → <b>OPEN</b> → in progress → fixed → to test → closed</>}
          states={DEFECT_LIFECYCLE_STATES}
          counts={defectStatusCounts}
        />
      </div>

      <div className="uv-detail-card">
        <div className="uv-detail-head">
          <h3>{card?.label}</h3>
          {!loading && (
            <span className="uv-detail-hint">
              <b>{rows.length}</b> record{rows.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        {loading ? (
          <div className="uv-state-loading">Loading…</div>
        ) : (
          <UVTable cols={cols} rows={rows} emptyMsg="No records found." onRowClick={onRowClick} />
        )}
      </div>
    </div>
  );
}
